import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
const inputVariants = cva("flex w-full border border-input bg-input-bg shadow-xs transition-[color,box-shadow] file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-ring/20 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50", {
  variants: {
    size: {
      md: "h-11 rounded-full px-4 py-1 text-sm md:text-base",
      sm: "h-9 rounded-full px-3 py-1 text-sm",
      xs: "h-7 rounded-md px-2 py-0.5 text-xs"
    },
    variant: {
      default: "",
      outline: "bg-input-bg",
      none: "bg-transparent border-0 p-0 h-auto rounded-none"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "md"
  }
});
const inputContainerVariants = cva("flex items-center w-full border border-input bg-input-bg gap-2 shadow-xs transition-[color,box-shadow] focus-within:outline-hidden focus-within:ring-ring/20 focus-within:ring-[3px] has-[input[aria-invalid=true]]:ring-destructive/20 has-[input[aria-invalid=true]]:border-destructive has-[input:disabled]:opacity-50 has-[input:disabled]:cursor-not-allowed", {
  variants: {
    size: {
      md: "h-11 rounded-full px-4 text-sm md:text-base",
      sm: "h-9 rounded-full px-3 text-sm",
      xs: "h-7 rounded-md px-2 text-xs gap-1.5"
    },
    variant: {
      default: "",
      outline: "bg-input-bg",
      none: "bg-transparent border-0 shadow-none"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "md"
  }
});
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">, VariantProps<typeof inputVariants> {
  /** Slot rendered before the input — typically an icon or short prefix ("$", "https://"). */
  leading?: React.ReactNode;
  /** Slot rendered after the input — typically an icon button, copy action, or trailing dropdown. */
  trailing?: React.ReactNode;
  /** Override class on the wrapper when slots are present. */
  containerClassName?: string;
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  className,
  containerClassName,
  type = "text",
  variant,
  size,
  leading,
  trailing,
  ...props
}, ref) => {
  if (!leading && !trailing) {
    return <input type={type} className={cn(inputVariants({
      variant,
      size,
      className
    }))} ref={ref} {...props} />;
  }
  return <div className={cn(inputContainerVariants({
    variant,
    size
  }), containerClassName)}>
        {leading && <span className="flex items-center text-muted-foreground shrink-0 [&_svg]:size-4">
            {leading}
          </span>}
        <input type={type} ref={ref} className={cn("flex-1 min-w-0 bg-transparent border-0 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed", className)} {...props} />
        {trailing && <span className="flex items-center shrink-0 [&_svg]:size-4">
            {trailing}
          </span>}
      </div>;
});
Input.displayName = "Input";
export { Input, inputVariants, inputContainerVariants };